import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { backgroundSlotsCount, productPosesCount } from '../../../imageRecords';
import { productPosesFixed } from '../../../perPoseBackgrounds';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../../../../shared/protectedImage';
import { imageIntentGalleryOf } from '../../../../shared/protectedImage/imageIntentGallery';
import type { ImageIntentContext } from '../../../../../../utils/imageIntentReporter';

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #439be2;
  border-radius: 999px;
  color: #1769aa;
  font-weight: 700;
  margin: 0 auto 18px;
  padding: 8px 14px;
  width: fit-content;
`;

const PoseThumb = styled.span`
  display: inline-block;
  height: 40px;
  margin-right: 10px;
  overflow: hidden;
  position: relative;
  width: 32px;
`;

const PoseThumbImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const EditingIndicator = () => {
  const activeConfigSlotIndex = useSelector(
    (state: any) => state.productConfig.activeConfigSlotIndex,
  );
  const activeConfigStep = useSelector(
    (state: any) => state.productConfig.activeConfigStep,
  );
  const backgroundSelections = useSelector(
    (state: any) => state.productConfig.backgroundSelections,
  );
  const poseSelections = useSelector(
    (state: any) => state.productConfig.poseSelections,
  );
  const product = useSelector(
    (state: any) => state.productConfig.currentProduct,
  );
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'pose_slot',
  });

  const hasActiveConfigSlot =
    activeConfigSlotIndex !== null && activeConfigSlotIndex !== undefined;

  if (!hasActiveConfigSlot || !activeConfigStep) {
    return null;
  }

  const totalSlots =
    activeConfigStep === 'background'
      ? backgroundSlotsCount(product)
      : productPosesCount(product);
  const existingSelection =
    activeConfigStep === 'background'
      ? backgroundSelections[activeConfigSlotIndex]
      : poseSelections[activeConfigSlotIndex];
  const action = existingSelection ? 'Editing' : 'Selecting';
  const label = activeConfigStep === 'background' ? 'background' : 'pose';

  // A "background chosen per pose" product: each background slot belongs to
  // the gallery pose at the same index (perPoseBackgrounds).
  // The pose's thumbnail sits in the pill, so the parent always sees which
  // photo the background is for (a JPG pose is not layered over the grid).
  if (activeConfigStep === 'background' && productPosesFixed(product)) {
    const pose = poseSelections[activeConfigSlotIndex];
    const poseUrl: string | undefined = pose?.selected_crop?.url ?? pose?.url;
    const frictionContext: ImageIntentContext = {
      surface: 'pose_slot',
      imageKey: poseUrl ?? `slot-${activeConfigSlotIndex}`,
      ...imageIntentGalleryOf(pose),
    };
    return (
      <Pill>
        {poseUrl && (
          <PoseThumb>
            <PoseThumbImage
              {...getFrictionProps(frictionContext)}
              src={poseUrl}
              alt={`Pose ${activeConfigSlotIndex + 1}`}
            />
            <ImageProtectionOverlay
              context={frictionContext}
              imageSrc={poseUrl}
            />
          </PoseThumb>
        )}
        {`${action} background for pose ${
          activeConfigSlotIndex + 1
        } of ${totalSlots}`}
      </Pill>
    );
  }

  return (
    <Pill>{`${action} ${label} ${
      activeConfigSlotIndex + 1
    } of ${totalSlots}`}</Pill>
  );
};

export default EditingIndicator;
